import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  TextField,
  Select,
  MenuItem,
  Button,
} from "@mui/material";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import SyncAltOutlinedIcon from "@mui/icons-material/SyncAltOutlined";

const formatFecha = (iso) => {
  if (!iso) return "(sin fecha)";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  const dd = pad(d.getDate());
  const mm = pad(d.getMonth() + 1);
  const yy = String(d.getFullYear()).slice(2);
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${dd}/${mm}/${yy} ${hh}:${mi}`;
};

const TOPICO_TITLE_LABELS = {
  INICIO: "Inicio",
  INFORMACION: "Información",
  PRECIO: "Precio",
  NEGOCIACION: "Negociación",
  SEGUIMIENTO: "Seguimiento",
  INTERES: "Interés",
};

const TOPICO_UPPER_LABELS = {
  INICIO: "INICIO",
  INFORMACION: "INFORMACIÓN",
  PRECIO: "PRECIO",
  NEGOCIACION: "NEGOCIACIÓN",
  SEGUIMIENTO: "SEGUIMIENTO",
  INTERES: "INTERÉS",
};

const formatTopico = (topico) => {
  const key = String(topico || "").toUpperCase();
  return TOPICO_TITLE_LABELS[key] || String(topico || "");
};

const formatTopicoSelect = (topico) => {
  const key = String(topico || "").toUpperCase();
  return TOPICO_UPPER_LABELS[key] || String(topico || "").toUpperCase();
};

const TYPE_LABELS = {
  MANUAL: "Manual",
  CAMBIO_ESTADO: "Cambio de estado",
  FUTURO_PROGRAMADO: "Futuro programado",
  REASIGNACION: "Reasignación",
  REACTIVACION: "Reactivación",
  VENTA_REGISTRADA: "Venta registrada",
};

const TYPE_META = {
  MANUAL: {
    color: "#2563eb",
    bg: "#eff6ff",
    border: "#bfdbfe",
    chipBg: "#dbeafe",
    chipColor: "#1d4ed8",
    Icon: InfoOutlinedIcon,
  },
  CAMBIO_ESTADO: {
    color: "#f97316",
    bg: "#fff7ed",
    border: "#fed7aa",
    chipBg: "#ffedd5",
    chipColor: "#c2410c",
    Icon: SwapHorizOutlinedIcon,
  },
  FUTURO_PROGRAMADO: {
    color: "#7c3aed",
    bg: "#f5f3ff",
    border: "#ddd6fe",
    chipBg: "#ede9fe",
    chipColor: "#6d28d9",
    Icon: EventAvailableOutlinedIcon,
  },
  REASIGNACION: {
    color: "#0891b2",
    bg: "#ecfeff",
    border: "#a5f3fc",
    chipBg: "#cffafe",
    chipColor: "#0e7490",
    Icon: SyncAltOutlinedIcon,
  },
  REACTIVACION: {
    color: "#16a34a",
    bg: "#f0fdf4",
    border: "#bbf7d0",
    chipBg: "#dcfce7",
    chipColor: "#15803d",
    Icon: ReplayOutlinedIcon,
  },
  VENTA_REGISTRADA: {
    color: "#db2777",
    bg: "#fdf2f8",
    border: "#fbcfe8",
    chipBg: "#fce7f3",
    chipColor: "#be185d",
    Icon: SellOutlinedIcon,
  },
};

const normalizeTipo = (tipo) => String(tipo || "MANUAL").toUpperCase();

const formatTipo = (tipo) => {
  const key = normalizeTipo(tipo);
  return TYPE_LABELS[key] || key.replaceAll("_", " ").toLowerCase();
};

const getTipoMeta = (tipo) => TYPE_META[normalizeTipo(tipo)] || TYPE_META.MANUAL;

const getChipLabel = (devolucion) => {
  const tipo = normalizeTipo(devolucion?.tipo);
  if (tipo !== "MANUAL") return formatTipo(tipo);
  return devolucion?.topico ? formatTopico(devolucion.topico) : "Sin estadio";
};

const MANUAL_COLORS = {
  color: "#1565c0",
  bg: "rgba(33, 150, 243, 0.08)",
  border: "rgba(33, 150, 243, 0.3)",
  chipBg: "rgba(33, 150, 243, 0.2)",
  chipColor: "#1565c0",
};

export default function DevolucionesPanel({
  open,
  onClose,
  devoluciones = [],
  loading = false,
  submitting = false,
  onSubmit,
  valueTexto,
  setValueTexto,
  valueTopico,
  setValueTopico,
  topicoOptions = [],
  readOnly = false,
  hideComposer = false,
  height = 500,
}) {
  const handleTextChange = (e) => {
    const v = e.target.value ?? "";
    setValueTexto(v.slice(0, 256));
  };

  const bottomRef = React.useRef(null);
  
  // Función que asegura el scroll al fondo (después de pintar)
  const scrollToBottom = React.useCallback(() => {
    // Espera a que MUI termine la animación / layout
    requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
    });
  }, []);
  
  // Al abrir el diálogo
  useEffect(() => {
    if (!open) return;
    // pequeño delay por la transición de Dialog
    const t = setTimeout(scrollToBottom, 50);
    return () => clearTimeout(t);
  }, [open, scrollToBottom]);

  // Cuando llegan nuevas devoluciones
  useEffect(() => {
    if (!open) return;
    scrollToBottom();
  }, [devoluciones.length, open, scrollToBottom]);

  const showInitialLoading = loading && devoluciones.length === 0;

  return (
    <Box
      sx={{
        p: 0,
        display: "flex",
        flexDirection: "column",
        height,
        minHeight: 0,
      }}
    >
      {/* Lista scrolleable */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 2, pb: 1.5 }}>
        <Stack spacing={0} sx={{ pt: 1.5 }}>
          {showInitialLoading && (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Cargando devoluciones...
            </Typography>
          )}

          {!showInitialLoading && devoluciones.length === 0 && (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              No hay devoluciones aún.
            </Typography>
          )}

          {!showInitialLoading &&
            devoluciones.map((d, index) => {
              const tipo = normalizeTipo(d.tipo);
              const isManual = tipo === "MANUAL";
              const isLast = index === devoluciones.length - 1;
              const colors = isManual ? MANUAL_COLORS : getTipoMeta(tipo);
              const Icon = isManual ? EditNoteOutlinedIcon : colors.Icon;
              return (
                <Box
                  key={d.id}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "34px 1fr",
                    gap: 1.5,
                    position: "relative",
                    pb: 1.6,
                    "&::before": {
                      content: '""',
                      display: isLast ? "none" : "block",
                      position: "absolute",
                      left: 15,
                      top: 40,
                      bottom: -8,
                      width: "1.5px",
                      bgcolor: "#dfe7f3",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      mt: 1,
                      color: colors.color,
                      bgcolor: colors.chipBg,
                      border: `1px solid ${colors.border}`,
                      zIndex: 1,
                    }}
                  >
                    <Icon sx={{ fontSize: 20 }} />
                  </Box>

                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderRadius: 1.5,
                      bgcolor: colors.bg,
                      border: `1px solid ${colors.border}`,
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "128px 1fr" },
                      gap: { xs: 1, sm: 2 },
                      alignItems: "center",
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{ display: "block", color: "#64748b", fontWeight: 600 }}
                      >
                        {formatFecha(d.fecha || d.created_at) || "(sin fecha)"}
                      </Typography>
                      <Chip
                        size="small"
                        label={getChipLabel(d)}
                        sx={{
                          mt: 0.5,
                          height: 20,
                          borderRadius: 1,
                          bgcolor: colors.chipBg,
                          color: colors.chipColor,
                          fontSize: 11,
                          fontWeight: 800,
                          "& .MuiChip-label": {
                            px: 0.9,
                          },
                        }}
                      />
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        color: "#14213d",
                        fontSize: 12,
                        lineHeight: 1,
                        fontWeight: 600,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {d.devolucion}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          <div ref={bottomRef} />
        </Stack>
      </Box>

      {!hideComposer && (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            bgcolor: "#fff",
            borderTop: "1px solid #e5e7eb",
            px: 2,
            py: 1,
            boxShadow: "0 -2px 8px rgba(15, 23, 42, 0.04)",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ sm: "stretch" }}
          >
            <TextField
              fullWidth
              placeholder={readOnly ? "Solo lectura" : "Escribe una devolución"}
              value={valueTexto}
              onChange={handleTextChange}
              multiline
              minRows={1}
              maxRows={6}
              inputProps={{ maxLength: 256 }}
              disabled={readOnly}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                  fontSize: 12,
                  minHeight: 56,
                },
              }}
            />
            <Select
              value={valueTopico}
              onChange={(e) => setValueTopico(e.target.value)}
              displayEmpty
              renderValue={(v) => (v ? formatTopicoSelect(v) : "SIN ESTADIO")}
              sx={{
                minWidth: { xs: "100%", sm: 180 },
                borderRadius: 1,
                fontSize: 12,
                fontWeight: 700,
              }}
              disabled={readOnly}
            >
              {topicoOptions.map((opt, idx) => (
                <MenuItem
                  key={(opt.value || "__empty") + "-" + idx}
                  value={opt.value}
                >
                  {opt.label}
                </MenuItem>
              ))}
            </Select>

            <Button
              variant="contained"
              size="large"
              sx={{
                px: 2.3,
                minWidth: 86,
                borderRadius: 1,
                boxShadow: "none",
              }}
              onClick={onSubmit}
              disabled={readOnly || loading || !valueTexto?.trim()}
            >
              {submitting ? "Enviando..." : "Enviar"}
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
